/**
 * A confirm dialog that will not act without a reason — for the third-party
 * fulfilment controls whose every change is recorded with who, when and why:
 * showing or hiding supplier pricing (#90), suspending a supplier (#87) and
 * recording a purchase order's cancellation (#82).
 */
import { useState, type ReactNode } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../primitives/alert-dialog";
import { buttonVariants } from "../../primitives/button";
import { Label } from "../../primitives/label";
import { Textarea } from "../../primitives/textarea";

export function ReasonConfirmDialog({
  trigger,
  open: controlledOpen,
  onOpenChange,
  title,
  description,
  details,
  reasonLabel,
  confirmLabel,
  cancelLabel = "Cancel",
  destructive = false,
  disabled = false,
  onConfirm,
}: {
  /**
   * The button that opens the dialog (rendered as the trigger's child). Omit
   * it and pass `open`/`onOpenChange` when the opener is a control whose own
   * `data-state` must survive — a Switch, which a trigger would overwrite.
   */
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title: string;
  /** The consequence, in business terms. */
  description: ReactNode;
  /** What exactly is changing, as a list or table, between the description and the reason. */
  details?: ReactNode;
  reasonLabel: string;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  disabled?: boolean;
  onConfirm: (reason: string) => void;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const [reason, setReason] = useState("");
  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        setUncontrolledOpen(next);
        onOpenChange?.(next);
        if (!next) setReason("");
      }}
    >
      {trigger && (
        <AlertDialogTrigger asChild disabled={disabled}>
          {trigger}
        </AlertDialogTrigger>
      )}
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {details}
        <div className="space-y-1.5">
          <Label htmlFor="reason-confirm-reason">{reasonLabel}</Label>
          <Textarea
            id="reason-confirm-reason"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            disabled={reason.trim() === ""}
            className={
              destructive
                ? buttonVariants({ variant: "destructive" })
                : undefined
            }
            onClick={() => onConfirm(reason.trim())}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
